/**
 * 문서 미리보기 페이지 수 계산 유틸리티
 * - editorContent (HTML 문자열)을 기반으로 페이지 수 계산
 * - 실제 DocumentPreview 레이아웃과 동일한 조건으로 높이 측정
 */

// A4 페이지 크기 (mm를 px로 변환, 1mm ≈ 3.779527559px)
const MM_TO_PX = 3.779527559;
const A4_HEIGHT_MM = 297;
const A4_WIDTH_MM = 210;
const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX; // ≈ 1122.52px
const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX; // ≈ 793.70px

// DocumentPreview.module.scss와 동기화된 레이아웃 상수
const PREVIEW_CONTENT_PADDING = { top: 15, bottom: 15, left: 20, right: 20 };
const SECTION_GAP = 10; // previewContent의 gap
const EDITOR_CONTENT_PADDING = 20; // editorContent의 상하 패딩

// 실제 에디터 콘텐츠가 들어갈 수 있는 너비 (A4 - 좌우 패딩)
const CONTENT_WIDTH_PX = A4_WIDTH_PX - PREVIEW_CONTENT_PADDING.left - PREVIEW_CONTENT_PADDING.right;

/**
 * 임시 측정용 컨테이너 생성
 * DocumentPreview의 editorContent와 동일한 스타일 적용
 * @returns {HTMLElement} 임시 컨테이너
 */
function createMeasureContainer() {
  const container = document.createElement('div');
  container.style.cssText = `
    position: absolute;
    visibility: hidden;
    left: -9999px;
    top: -9999px;
    width: ${CONTENT_WIDTH_PX}px;
    padding: ${EDITOR_CONTENT_PADDING}px;
    box-sizing: border-box;
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif; // CKEditor 기본 폰트 설정 반영
    font-size: 14px;
    line-height: 1.5; // CKEditorWrapper.module.scss의 1.5 반영
    word-wrap: break-word;
    overflow: visible;
  `;
  // CKEditor의 p 태그 스타일 (margin: 0) 반영을 위한 전역 스타일 추가 제안
  // 여기서는 컨테이너 내부의 p 태그에 직접 적용
  container.innerHTML = `<style>
    div.ck-content-measure p { margin: 0 !important; line-height: 1.5 !important; }
    div.ck-content-measure .table { margin: 0 20px !important; }
  </style>`;
  container.className = 'ck-content-measure';
  return container;
}

/**
 * HTML 문자열의 실제 높이를 측정
 * @param {string} htmlContent - HTML 문자열
 * @returns {number} 콘텐츠 높이 (px) - 패딩 포함
 */
function measureHtmlContentHeight(htmlContent) {
  if (!htmlContent || htmlContent.trim() === '') {
    return 0;
  }

  const tempElement = createMeasureContainer();
  tempElement.innerHTML = htmlContent;
  document.body.appendChild(tempElement);

  // 강제 리플로우 후 높이 측정
  void tempElement.offsetHeight;
  const height = tempElement.scrollHeight || tempElement.offsetHeight || 0;

  document.body.removeChild(tempElement);
  return height;
}

/**
 * 페이지 레이아웃 정보 계산
 * 첫 페이지, 중간 페이지, 마지막 페이지의 에디터 가용 높이를 반환
 * @param {Object} params - 계산 파라미터
 * @returns {Object} 페이지별 가용 높이 정보
 */
function calculatePageLayout({ useCorporateSeal, summaryNote, attachments, referenceDocuments }) {
  // A4 페이지 내 사용 가능한 총 높이 (상하 패딩 제외)
  const usableHeight = A4_HEIGHT_PX - PREVIEW_CONTENT_PADDING.top - PREVIEW_CONTENT_PADDING.bottom;

  // 각 섹션별 고정 높이 정의 (실제 렌더링 크기에 맞춰 최적화)
  const FIRST_PAGE_HEADER = 45; // 제목 영역 (60 -> 45)
  const FIRST_PAGE_TOP_SECTION = 165; // 기안정보 + 결재라인 (200 -> 165)
  const FIRST_PAGE_SECURITY_CHECKBOX = useCorporateSeal ? 25 : 0;
  const FIRST_PAGE_MIDDLE_SECTION = 85; // 참조/시행/제목 (100 -> 85)
  const PAGE_FOOTER = 40; // 푸터 영역 (50 -> 40)
  const EDITOR_BORDER = 2;

  const SECTION_GAP = 10;
  const DEFAULT_SAFETY_MARGIN = 15; // 안전 마진 축소 (20 -> 15)

  // 하단 콘텐츠 높이 합계 (마지막 페이지용)
  let bottomContentHeight = 0;
  if (summaryNote && summaryNote.trim() !== '') bottomContentHeight += 100;
  else bottomContentHeight += 50;
  if (attachments && attachments.length > 0)
    bottomContentHeight += 50 + Math.min(attachments.length, 5) * 30;
  else bottomContentHeight += 50;
  if (referenceDocuments && referenceDocuments.length > 0)
    bottomContentHeight += 60 + Math.min(referenceDocuments.length, 3) * 40;
  else bottomContentHeight += 50;

  // 1. 첫 페이지 고정 높이
  const firstPageFixedGaps = 4 * SECTION_GAP;
  const firstPageFixedHeight =
    FIRST_PAGE_HEADER +
    FIRST_PAGE_TOP_SECTION +
    FIRST_PAGE_SECURITY_CHECKBOX +
    FIRST_PAGE_MIDDLE_SECTION +
    firstPageFixedGaps +
    DEFAULT_SAFETY_MARGIN;

  // 2. 중간 페이지 고정 높이 (최소 마진만 유지)
  const subsequentFixedHeight = DEFAULT_SAFETY_MARGIN;

  // 3. 마지막 페이지 추가 고정 높이 (푸터 및 하단 섹션)
  const lastPageFooterHeight = PAGE_FOOTER + SECTION_GAP;
  const lastPageExtraGaps = 2 * SECTION_GAP;

  return {
    // 단일 페이지 에디터 높이 (모든 요소 포함)
    singlePageEditorHeight:
      usableHeight -
      firstPageFixedHeight -
      bottomContentHeight -
      lastPageExtraGaps -
      lastPageFooterHeight -
      EDITOR_BORDER,
    // 다중 페이지 첫 장 (푸터 없음)
    firstPageEditorHeight: usableHeight - firstPageFixedHeight - EDITOR_BORDER,
    // 다중 페이지 중간 장 (가용 공간 최대화)
    middlePageEditorHeight: usableHeight - subsequentFixedHeight - EDITOR_BORDER,
    // 다중 페이지 마지막 장 (푸터 포함)
    lastPageEditorHeight:
      usableHeight -
      subsequentFixedHeight -
      bottomContentHeight -
      lastPageExtraGaps -
      lastPageFooterHeight -
      EDITOR_BORDER,
    bottomContentHeight,
  };
}

/**
 * 문서 미리보기 페이지 수 계산
 * @param {Object} params - 계산에 필요한 파라미터
 * @returns {number} 페이지 수 (최소 1)
 */
function calculatePageCount({
  editorContent,
  documentInfo,
  summaryNote,
  attachments,
  referenceDocuments,
  useCorporateSeal,
  returnLayout = false,
}) {
  // 에디터 콘텐츠 높이 측정
  const editorContentHeight = measureHtmlContentHeight(editorContent);

  // 페이지 레이아웃 정보 계산
  const layout = calculatePageLayout({
    useCorporateSeal,
    summaryNote,
    attachments,
    referenceDocuments,
  });

  if (returnLayout) {
    return { layout, editorContentHeight };
  }

  if (editorContentHeight === 0) {
    return 1;
  }

  // 단일 페이지로 충분한지 확인
  if (editorContentHeight <= layout.singlePageEditorHeight) {
    return 1;
  }

  // 다중 페이지 계산
  let remainingHeight = editorContentHeight - layout.firstPageEditorHeight;
  let pageCount = 1;

  while (remainingHeight > 0) {
    pageCount++;
    // 마지막 페이지가 될 수 있는지 확인
    if (remainingHeight <= layout.lastPageEditorHeight) {
      break;
    }
    // 중간 페이지로 처리
    remainingHeight -= layout.middlePageEditorHeight;
  }

  return pageCount;
}

/**
 * DOM 노드를 HTML 문자열로 변환
 * @param {Node} node - DOM 노드
 * @returns {string} HTML 문자열
 */
function nodeToHtml(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    return node.outerHTML;
  }
  return '';
}

/**
 * 분리 불가 요소인지 확인 (이미지, figure 등)
 * 테이블은 행 단위로 분리 가능하므로 제외
 * @param {Node} node - DOM 노드
 * @returns {boolean}
 */
function isAtomicElement(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const tagName = node.tagName.toLowerCase();
  // 테이블은 행 단위로 분리 가능하므로 제외
  return tagName === 'img' || tagName === 'figure';
}

/**
 * 테이블인지 확인
 * @param {Node} node - DOM 노드
 * @returns {boolean}
 */
function isTableElement(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  return node.tagName.toLowerCase() === 'table';
}

/**
 * 블록 요소인지 확인 (내부 텍스트 분할 가능)
 * @param {Node} node - DOM 노드
 * @returns {boolean}
 */
function isBlockElement(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const tagName = node.tagName.toLowerCase();
  return ['p', 'div', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre'].includes(
    tagName,
  );
}

/**
 * 테이블을 행 단위로 분할
 * @param {Element} tableElement - 분할할 테이블 요소
 * @param {HTMLElement} measureContainer - 측정용 컨테이너
 * @param {number} availableHeight - 현재 페이지 가용 높이
 * @param {string} currentPageHtml - 현재 페이지에 이미 있는 HTML
 * @returns {{first: string, remaining: string}} 분할된 HTML
 */
function splitTableElement(tableElement, measureContainer, availableHeight, currentPageHtml) {
  // 테이블의 속성 복사
  const attrs = Array.from(tableElement.attributes)
    .map(function (attr) {
      return `${attr.name}="${attr.value}"`;
    })
    .join(' ');
  const tableOpen = attrs ? `<table ${attrs}>` : '<table>';
  const tableClose = '</table>';

  // thead, tbody, tfoot 분리
  const thead = tableElement.querySelector('thead');
  const tbody = tableElement.querySelector('tbody');
  const tfoot = tableElement.querySelector('tfoot');

  // tbody가 없으면 직접 tr을 찾음
  let rows = [];
  if (tbody) {
    rows = Array.from(tbody.querySelectorAll('tr'));
  } else {
    rows = Array.from(tableElement.querySelectorAll('tr')).filter(function (tr) {
      // thead나 tfoot에 속하지 않는 tr만
      return !tr.closest('thead') && !tr.closest('tfoot');
    });
  }

  const theadHtml = thead ? thead.outerHTML : '';
  const tfootHtml = tfoot ? tfoot.outerHTML : '';

  // 헤더만 있는 테이블의 기본 높이
  let firstTableRows = [];
  let splitIndex = 0;

  // 행을 하나씩 추가하며 높이 확인
  for (let i = 0; i < rows.length; i++) {
    const rowsHtml = firstTableRows
      .map(function (r) {
        return r.outerHTML;
      })
      .join('');
    const testRowsHtml = rowsHtml + rows[i].outerHTML;

    let testTableHtml;
    if (tbody) {
      testTableHtml = tableOpen + theadHtml + '<tbody>' + testRowsHtml + '</tbody>' + tableClose;
    } else {
      testTableHtml = tableOpen + theadHtml + testRowsHtml + tableClose;
    }

    const testHtml = currentPageHtml + testTableHtml;
    measureContainer.innerHTML = testHtml;
    void measureContainer.offsetHeight;
    const testHeight = measureContainer.scrollHeight || measureContainer.offsetHeight || 0;

    if (testHeight <= availableHeight) {
      firstTableRows.push(rows[i]);
      splitIndex = i + 1;
    } else {
      // 첫 행도 안 들어가면
      if (i === 0 && currentPageHtml.trim() === '') {
        // 페이지가 비어있으면 최소 첫 행은 넣음
        firstTableRows.push(rows[0]);
        splitIndex = 1;
      }
      break;
    }
  }

  // 분할 결과 생성
  if (splitIndex === 0) {
    // 현재 페이지에 들어갈 수 없음 - 전체를 다음 페이지로
    return { first: '', remaining: tableElement.outerHTML };
  }

  if (splitIndex >= rows.length) {
    // 전체가 들어감
    return { first: tableElement.outerHTML, remaining: '' };
  }

  // 첫 번째 테이블 (현재 페이지용)
  const firstRowsHtml = firstTableRows
    .map(function (r) {
      return r.outerHTML;
    })
    .join('');
  let firstTableHtml;
  if (tbody) {
    firstTableHtml = tableOpen + theadHtml + '<tbody>' + firstRowsHtml + '</tbody>' + tableClose;
  } else {
    firstTableHtml = tableOpen + theadHtml + firstRowsHtml + tableClose;
  }

  // 남은 테이블 (다음 페이지용) - 헤더 포함하여 연속성 유지
  const remainingRows = rows.slice(splitIndex);
  const remainingRowsHtml = remainingRows
    .map(function (r) {
      return r.outerHTML;
    })
    .join('');
  let remainingTableHtml;
  if (tbody) {
    remainingTableHtml =
      tableOpen + theadHtml + '<tbody>' + remainingRowsHtml + '</tbody>' + tfootHtml + tableClose;
  } else {
    remainingTableHtml = tableOpen + theadHtml + remainingRowsHtml + tfootHtml + tableClose;
  }

  return { first: firstTableHtml, remaining: remainingTableHtml };
}

/**
 * 블록 요소의 텍스트를 분할하여 두 개의 요소로 나눔
 * @param {Element} element - 분할할 블록 요소
 * @param {HTMLElement} measureContainer - 측정용 컨테이너
 * @param {number} availableHeight - 현재 페이지 가용 높이
 * @param {string} currentPageHtml - 현재 페이지에 이미 있는 HTML
 * @returns {{first: string, remaining: string}} 분할된 HTML
 */
function splitBlockElement(element, measureContainer, availableHeight, currentPageHtml) {
  const tagName = element.tagName.toLowerCase();
  const textContent = element.textContent || '';

  // 텍스트가 없으면 분할 불가
  if (!textContent.trim()) {
    return { first: element.outerHTML, remaining: '' };
  }

  // 단어 단위로 분할
  const words = textContent.split(/(\s+)/); // 공백 포함하여 분할
  let firstPart = '';
  let splitIndex = 0;

  // 요소의 속성 복사
  const attrs = Array.from(element.attributes)
    .map(function (attr) {
      return `${attr.name}="${attr.value}"`;
    })
    .join(' ');
  const openTag = attrs ? `<${tagName} ${attrs}>` : `<${tagName}>`;
  const closeTag = `</${tagName}>`;

  // 단어를 하나씩 추가하며 높이 확인
  for (let i = 0; i < words.length; i++) {
    const testText = firstPart + words[i];
    const testHtml = currentPageHtml + openTag + testText + closeTag;

    measureContainer.innerHTML = testHtml;
    void measureContainer.offsetHeight;
    const testHeight = measureContainer.scrollHeight || measureContainer.offsetHeight || 0;

    if (testHeight <= availableHeight) {
      firstPart = testText;
      splitIndex = i + 1;
    } else {
      // 첫 단어도 안 들어가면 현재 페이지에 아무것도 넣지 않음
      if (i === 0 && currentPageHtml.trim() === '') {
        // 페이지가 비어있으면 최소 첫 단어는 넣음
        firstPart = words[0];
        splitIndex = 1;
      }
      break;
    }
  }

  // 분할 결과 생성
  if (splitIndex === 0) {
    // 현재 페이지에 들어갈 수 없음 - 전체를 다음 페이지로
    return { first: '', remaining: element.outerHTML };
  }

  if (splitIndex >= words.length) {
    // 전체가 들어감
    return { first: element.outerHTML, remaining: '' };
  }

  const firstHtml = openTag + firstPart.trim() + closeTag;
  const remainingText = words.slice(splitIndex).join('').trim();
  const remainingHtml = remainingText ? openTag + remainingText + closeTag : '';

  return { first: firstHtml, remaining: remainingHtml };
}

/**
 * 에디터 콘텐츠를 페이지별로 분리
 * - 노드 단위 분할 + 블록 요소 내부 텍스트 분할
 * - 긴 텍스트도 페이지 경계에서 정확히 분할
 *
 * @param {Object} params - 분리에 필요한 파라미터
 * @returns {Array<string>} 페이지별 HTML 콘텐츠 배열
 */
function splitEditorContentByPage({
  editorContent,
  totalPages,
  documentInfo,
  useCorporateSeal,
  summaryNote,
  attachments,
  referenceDocuments,
}) {
  // 단일 페이지이거나 콘텐츠가 없으면 그대로 반환
  if (!editorContent || editorContent.trim() === '' || totalPages <= 1) {
    return [editorContent || ''];
  }

  // 페이지 레이아웃 정보 계산
  const layout = calculatePageLayout({
    useCorporateSeal,
    summaryNote,
    attachments,
    referenceDocuments,
  });

  // 임시 측정용 컨테이너 생성
  const measureContainer = createMeasureContainer();
  document.body.appendChild(measureContainer);

  // HTML을 DOM으로 파싱하여 자식 노드 추출
  const wrapper = document.createElement('div');
  wrapper.innerHTML = editorContent;

  // 빈 텍스트 노드 제거
  const allNodes = Array.from(wrapper.childNodes).filter(function (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent && node.textContent.trim() !== '';
    }
    return true;
  });

  const pageContents = []; // 페이지별 HTML 배열
  let currentPageHtml = ''; // 현재 페이지 HTML
  let currentPageNumber = 1;

  /**
   * 현재 페이지의 가용 높이 계산
   */
  function getAvailableHeight(pageNum) {
    if (pageNum === 1) {
      if (totalPages === 1) {
        return layout.singlePageEditorHeight;
      }
      return layout.firstPageEditorHeight;
    }
    if (pageNum >= totalPages) {
      return layout.lastPageEditorHeight;
    }
    return layout.middlePageEditorHeight;
  }

  /**
   * 현재 페이지 HTML의 높이 측정
   */
  function measureHeight(html) {
    measureContainer.innerHTML = html;
    void measureContainer.offsetHeight;
    return measureContainer.scrollHeight || measureContainer.offsetHeight || 0;
  }

  /**
   * 현재 페이지를 저장하고 다음 페이지로 이동
   */
  function finishCurrentPage() {
    if (currentPageHtml.trim()) {
      pageContents.push(currentPageHtml);
      currentPageHtml = '';
    }
    currentPageNumber++;
  }

  // 노드 순회하며 페이지별로 분배
  let nodeIndex = 0;
  while (nodeIndex < allNodes.length) {
    const node = allNodes[nodeIndex];
    const availableHeight = getAvailableHeight(currentPageNumber);
    const testHtml = currentPageHtml + nodeToHtml(node);
    const testHeight = measureHeight(testHtml);

    // 현재 페이지에 들어갈 수 있는지 확인
    if (testHeight <= availableHeight) {
      currentPageHtml = testHtml;
      nodeIndex++;
    } else {
      // 넘침 처리

      // 테이블 분할
      if (isTableElement(node)) {
        const splitResult = splitTableElement(
          node,
          measureContainer,
          availableHeight,
          currentPageHtml,
        );

        if (splitResult.first) {
          currentPageHtml += splitResult.first;
        }

        finishCurrentPage();

        if (splitResult.remaining) {
          const tempWrapper = document.createElement('div');
          tempWrapper.innerHTML = splitResult.remaining;
          const remainingNode = tempWrapper.firstChild;

          if (remainingNode) {
            const remainingHeight = measureHeight(nodeToHtml(remainingNode));
            const newAvailable = getAvailableHeight(currentPageNumber);

            if (remainingHeight <= newAvailable) {
              currentPageHtml = nodeToHtml(remainingNode);
            } else {
              // 아직도 넘침 - 계속 분할 필요
              allNodes.splice(nodeIndex, 1, remainingNode);
              continue;
            }
          }
        }
        nodeIndex++;
      }
      // 블록 요소 분할
      else if (isBlockElement(node) && !isAtomicElement(node)) {
        const splitResult = splitBlockElement(
          node,
          measureContainer,
          availableHeight,
          currentPageHtml,
        );

        if (splitResult.first) {
          currentPageHtml += splitResult.first;
        }

        finishCurrentPage();

        if (splitResult.remaining) {
          const tempWrapper = document.createElement('div');
          tempWrapper.innerHTML = splitResult.remaining;
          const remainingNode = tempWrapper.firstChild;

          if (remainingNode) {
            const remainingHeight = measureHeight(nodeToHtml(remainingNode));
            const newAvailable = getAvailableHeight(currentPageNumber);

            if (remainingHeight <= newAvailable) {
              currentPageHtml = nodeToHtml(remainingNode);
            } else {
              // 아직도 넘침 - 계속 분할 필요
              allNodes.splice(nodeIndex, 1, remainingNode);
              continue;
            }
          }
        }
        nodeIndex++;
      } else {
        // 분할 불가 요소 (이미지 등) 또는 현재 페이지가 비어있는 경우

        if (currentPageHtml.trim()) {
          // 현재 페이지에 콘텐츠가 있으면 저장하고 다음 페이지로
          finishCurrentPage();
          // nodeIndex 증가 없이 현재 노드를 다음 페이지에서 처리
        } else {
          // 현재 페이지가 비어있는데도 안 들어가면 강제로 추가
          currentPageHtml = nodeToHtml(node);
          nodeIndex++;

          // 아토믹 요소가 페이지를 초과하면 경고
          if (isAtomicElement(node)) {
            // 아토믹 요소가 페이지를 초과하는 경우, 개발 모드에서만 경고
          }
        }
      }
    }
  }

  // 마지막 페이지 저장
  if (currentPageHtml.trim()) {
    pageContents.push(currentPageHtml);
  }

  // 페이지 수 맞추기 (빈 페이지 추가)
  while (pageContents.length < totalPages) {
    pageContents.push('');
  }

  // 임시 컨테이너 제거
  document.body.removeChild(measureContainer);

  return pageContents;
}

/**
 * 이미지 기반 페이지 분할 정보 계산
 * @param {number} totalImageHeight - 캡처된 이미지의 전체 높이
 * @param {Object} layout - 페이지 레이아웃 정보
 * @returns {Array<{top: number, height: number, containerHeight: number}>} 페이지별 이미지 슬라이스 정보
 */
function calculateImageSlices(totalImageHeight, layout) {
  const slices = [];
  let remainingHeight = totalImageHeight;
  let currentTop = 0;

  // 1. 단일 페이지 여부 확인
  if (totalImageHeight <= layout.singlePageEditorHeight) {
    slices.push({
      top: 0,
      height: totalImageHeight,
      containerHeight: totalImageHeight,
    });
    return slices;
  }

  // 2. 다중 페이지 슬라이싱
  let currentPage = 1;
  while (remainingHeight > 0) {
    let availableHeight = 0;

    if (currentPage === 1) {
      // 첫 페이지: 항상 하단 섹션이 없다고 가정 (다중 페이지이므로)
      availableHeight = layout.firstPageEditorHeight;
    } else {
      // 마지막 페이지 후보인지 확인
      if (remainingHeight <= layout.lastPageEditorHeight) {
        availableHeight = layout.lastPageEditorHeight;
      } else {
        // 중간 페이지
        availableHeight = layout.middlePageEditorHeight;
      }
    }

    const sliceHeight = Math.min(remainingHeight, availableHeight);

    slices.push({
      top: Math.round(currentTop),
      height: Math.round(sliceHeight),
      containerHeight: Math.round(sliceHeight),
    });

    currentTop += sliceHeight;
    remainingHeight -= sliceHeight;
    currentPage++;

    if (currentPage > 50) break;
  }

  return slices;
}

export { splitEditorContentByPage, calculateImageSlices };
export default calculatePageCount;
